import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Badge,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Banner,
  useIndexResourceState,
  InlineStack,
  Box,
  Thumbnail,
} from '@shopify/polaris';
import {
  bannerService,
  type Banner as BannerType,
  type CreateBannerInput,
  type ContentStatus,
} from '../../services/content.service';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

export function BannerManagementPage() {
  const [banners, setBanners] = useState<BannerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerType | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [status, setStatus] = useState<ContentStatus>('ACTIVE');

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(banners as unknown as {[key: string]: unknown}[]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await bannerService.list({ limit: 100 });
      setBanners(result.data);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setTitle('');
    setImageUrl('');
    setLinkUrl('');
    setDisplayOrder('0');
    setStatus('ACTIVE');
    setEditingBanner(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (banner: BannerType) => {
    setEditingBanner(banner);
    setTitle(banner.title);
    setImageUrl(banner.imageUrl);
    setLinkUrl(banner.linkUrl || '');
    setDisplayOrder(banner.displayOrder.toString());
    setStatus(banner.status);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!title.trim() || !imageUrl.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const data: CreateBannerInput = {
        title: title.trim(),
        imageUrl: imageUrl.trim(),
        linkUrl: linkUrl.trim() || undefined,
        displayOrder: parseInt(displayOrder) || 0,
        status,
      };

      if (editingBanner) {
        await bannerService.update(editingBanner.id, data);
      } else {
        await bannerService.create(data);
      }

      setShowModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      setError('Failed to save banner');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this banner?')) return;

    try {
      await bannerService.delete(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete banner');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedResources.length} banners?`)) return;

    try {
      await Promise.all(selectedResources.map((id) => bannerService.delete(id)));
      clearSelection();
      await loadData();
    } catch (err) {
      setError('Failed to delete banners');
      console.error(err);
    }
  };

  const rowMarkup = banners.map((banner, index) => (
    <IndexTable.Row
      id={banner.id}
      key={banner.id}
      selected={selectedResources.includes(banner.id)}
      position={index}
    >
      <IndexTable.Cell>
        <Thumbnail
          source={banner.imageUrl}
          alt={banner.title}
          size="medium"
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {banner.title}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {banner.linkUrl ? (
          <Text variant="bodySm" as="span">
            {banner.linkUrl.slice(0, 40)}
            {banner.linkUrl.length > 40 ? '...' : ''}
          </Text>
        ) : (
          <Text variant="bodySm" tone="subdued" as="span">
            No link
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {banner.displayOrder}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={banner.status === 'ACTIVE' ? 'success' : undefined}>
          {banner.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => openEditModal(banner)}>
            Edit
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDelete(banner.id)}>
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const bulkActions = [
    {
      content: 'Delete selected',
      onAction: handleBulkDelete,
      destructive: true,
    },
  ];

  return (
    <Page
      title="Banner Management"
      primaryAction={{
        content: 'Add Banner',
        onAction: openCreateModal,
      }}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Box>
      )}

      <Card padding="0">
        <IndexTable
          resourceName={{ singular: 'banner', plural: 'banners' }}
          itemCount={banners.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          bulkActions={bulkActions}
          headings={[
            { title: 'Image' },
            { title: 'Title' },
            { title: 'Link' },
            { title: 'Order' },
            { title: 'Status' },
            { title: 'Actions' },
          ]}
          loading={loading}
        >
          {rowMarkup}
        </IndexTable>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingBanner ? 'Edit Banner' : 'Add Banner'}
        primaryAction={{
          content: editingBanner ? 'Save Changes' : 'Create Banner',
          loading: saving,
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Title"
              value={title}
              onChange={setTitle}
              autoComplete="off"
              requiredIndicator
              helpText="Internal name for the banner"
            />
            <TextField
              label="Image URL"
              value={imageUrl}
              onChange={setImageUrl}
              autoComplete="off"
              requiredIndicator
              helpText="URL to the banner image"
            />
            {imageUrl && (
              <Box paddingBlockStart="200">
                <img
                  src={imageUrl}
                  alt="Banner preview"
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            )}
            <TextField
              label="Link URL"
              value={linkUrl}
              onChange={setLinkUrl}
              autoComplete="off"
              helpText="URL to navigate when banner is clicked (optional)"
            />
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={setDisplayOrder}
              autoComplete="off"
              helpText="Lower numbers appear first"
            />
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => setStatus(value as ContentStatus)}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
